import React, { useRef, useEffect, useState } from 'react';
import { Group, Circle, Shape, Text, Image as KonvaImage } from 'react-konva';
import type Konva from 'konva';
import { P } from '../../model/constants';

interface SmileyAvatarProps {
  x: number;          // Welt-X in Tile-Einheiten
  y: number;          // Welt-Y in Tile-Einheiten
  name: string;
  isPlayer?: boolean;
  isBot?: boolean;
  animate?: boolean;  // true für Remote-User (Konva-Tween)
  animateDuration?: number; // Tween-Dauer in Sekunden (default 0.1)
  videoElement?: HTMLVideoElement | null;
  profileImageEl?: HTMLImageElement | null;
  hoverLabel?: string;  // z.B. "Entwicklung · Senior Developer"
  onClick?: () => void;
  onContextMenu?: (screenX: number, screenY: number) => void;
  // Drag-to-Move (nur lokaler Spieler)
  draggable?: boolean;
  onDragStart?: () => void;
  onDragMove?: (e: Konva.KonvaEventObject<DragEvent>) => void;
  onDragEnd?:  (e: Konva.KonvaEventObject<DragEvent>) => void;
}

const SmileyAvatar = React.memo(({ x, y, name, isPlayer = false, isBot = false, animate = false, animateDuration = 0.1, videoElement, profileImageEl, hoverLabel, onClick, onContextMenu, draggable, onDragStart, onDragMove, onDragEnd }: SmileyAvatarProps) => {
  const groupRef = useRef<Konva.Group>(null);
  const [hovered, setHovered] = useState(false);

  // Initiale Position einmalig per Ref festhalten — so startet der Avatar
  // beim ersten Erscheinen sofort an der richtigen Stelle statt bei (0,0).
  // Nach dem Mount übernimmt der Tween die Positionskontrolle exklusiv;
  // React ändert die x/y-Props des Groups nicht mehr (initPos bleibt konstant).
  const initPos = useRef({ x: x * P, y: y * P });

  useEffect(() => {
    if (!animate || !groupRef.current) return;
    groupRef.current.to({ x: x * P, y: y * P, duration: animateDuration });
  }, [x, y, animate, animateDuration]);

  // animate-Modus: Props nur für die erste Positionierung, danach Tween
  const posX = animate ? initPos.current.x : x * P;
  const posY = animate ? initPos.current.y : y * P;

  return (
    <Group
      ref={groupRef}
      x={posX}
      y={posY}
      draggable={draggable}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onContextMenu={onContextMenu ? (e) => { e.evt.preventDefault(); onContextMenu(e.evt.clientX, e.evt.clientY); } : undefined}
      onMouseEnter={() => { document.body.style.cursor = (onClick || onContextMenu) ? 'pointer' : draggable ? 'grab' : 'default'; setHovered(true); }}
      onMouseLeave={() => { document.body.style.cursor = 'default'; setHovered(false); }}
      onDragStart={draggable ? () => { document.body.style.cursor = 'grabbing'; onDragStart?.(); } : undefined}
    >
      {/* Schatten */}
      <Circle radius={16} fill="rgba(0,0,0,0.18)" x={-2} y={4} />

      {videoElement ? (
        /* ── Video-Kreis-Modus (Radius 24, ragt über den Smiley-Ring hinaus) ── */
        <>
          <Group clipFunc={(ctx) => { ctx.arc(0, 0, 24, 0, Math.PI * 2); }}>
            <Shape
              listening={false}
              sceneFunc={(ctx) => {
                const v = videoElement;
                if (!v || v.readyState < 2) return;
                const vw = v.videoWidth;
                const vh = v.videoHeight;
                if (!vw || !vh) return;
                const s = 48 / Math.min(vw, vh);
                (ctx as any)._context.drawImage(v, -(vw * s) / 2, -(vh * s) / 2, vw * s, vh * s);
              }}
            />
          </Group>
          <Circle
            radius={24}
            fill="transparent"
            stroke={isPlayer ? '#ca8a04' : isBot ? '#16a34a' : '#2563eb'}
            strokeWidth={2}
          />
        </>
      ) : profileImageEl ? (
        /* ── Profilbild-Kreis-Modus ── */
        <>
          <Group clipFunc={(ctx) => { ctx.arc(0, 0, 24, 0, Math.PI * 2); }}>
            <KonvaImage image={profileImageEl} x={-24} y={-24} width={48} height={48} />
          </Group>
          <Circle
            radius={24}
            fill="transparent"
            stroke={isPlayer ? '#ca8a04' : isBot ? '#16a34a' : '#2563eb'}
            strokeWidth={2}
          />
        </>
      ) : (
        /* ── Smiley-Modus ── */
        <>
          {/* Gesicht */}
          <Circle
            radius={16}
            fill={isPlayer ? '#facc15' : isBot ? '#4ade80' : '#60a5fa'}
            stroke={isPlayer ? '#ca8a04' : isBot ? '#16a34a' : '#2563eb'}
            strokeWidth={2}
          />
          {/* Augen */}
          <Circle x={-5} y={-5} radius={2.5} fill="#1e293b" />
          <Circle x={5}  y={-5} radius={2.5} fill="#1e293b" />
          {/* Lächeln */}
          <Shape
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(-6, 3);
              ctx.quadraticCurveTo(0, 10, 6, 3);
              ctx.strokeShape(shape);
            }}
            stroke="#1e293b"
            strokeWidth={2.5}
            lineCap="round"
          />
          {/* Bot-Headset */}
          {isBot && (
            <>
              <Shape
                sceneFunc={(ctx, shape) => {
                  ctx.beginPath();
                  ctx.arc(0, -4, 19, Math.PI * 1.1, Math.PI * 1.9);
                  ctx.strokeShape(shape);
                }}
                stroke="#15803d"
                strokeWidth={3}
                lineCap="round"
              />
              <Circle x={-19} y={-4} radius={4} fill="#15803d" />
              <Circle x={19}  y={-4} radius={4} fill="#15803d" />
            </>
          )}
        </>
      )}

      {/* Spieler-Ring (immer sichtbar) */}
      {isPlayer && (
        <Circle radius={21} stroke="#fde68a" strokeWidth={1.5} dash={[4, 3]} opacity={0.7} />
      )}

      {/* Name-Label */}
      <Text
        text={name}
        fontSize={10}
        fill={isPlayer ? '#fef9c3' : isBot ? '#dcfce7' : '#dbeafe'}
        fontStyle={isPlayer || isBot ? 'bold' : 'normal'}
        x={-70} y={21}
        width={140}
        align="center"
      />

      {/* Hover-Tooltip: Abteilung · Berufsbezeichnung */}
      {hovered && hoverLabel && (
        <Text
          text={hoverLabel}
          fontSize={9}
          fill="#94a3b8"
          x={-80} y={34}
          width={160}
          align="center"
        />
      )}
    </Group>
  );
});

SmileyAvatar.displayName = 'SmileyAvatar';
export default SmileyAvatar;
